/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ProductDetailsAddToCart } from '@/components/blocks/ProductDetails/AddToCart';
import { ProductDetailsDisplay } from '@/components/blocks/ProductDetails/Display';
import { ProductDetailsPromos } from '@/components/blocks/ProductDetails/Promos';
import { ProductDetailsRibbonChips } from '@/components/blocks/ProductDetails/RibbonChips';
import { ProductDetailsSeller } from '@/components/blocks/ProductDetails/Seller';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { ContentProvider } from '@/data/context/content';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { ID } from '@/data/types/Basic';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect, useMemo, useState } from 'react';
import { ReleaseDateFormat } from '@/utils/Date';
import { ProductDetailsPrice } from '@/components/blocks/ProductDetails/Price';
import { StoreStockRv } from '@/components/content/StoreStockRv';
import PDPBazaarVoiceSummary from '@/components/blocks/PDPBazaarVoiceSummary';
import { ProductDetailsAddToWishList } from '@/components/blocks/ProductDetails/AddToWishList';
import { ccSectionObsRefSX, CloseStickA2CartBtnSX, PdpAddToCartSectionSX, PdpProductNameAndDetailsSX } from '@/components/content/PdpProductNameAndDetailsRv/styles/container';
import { PDPClickAndCollect } from '@/components/blocks/PDPClickAndCollect';
import { KlarnaPDP } from '@/components/content/KlarnaPDP';
import { useDeviceType } from '@/data/deviceDetection';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { getCookie, setCookie } from '@/utils/clickAndCollect';

export const PdpProductNameAndDetailsRv: FC<{
	id: ID;
}> = ({ id }) => {
	const {
		dimensions: { contentSpacing },
		zIndex
	} = useTheme();
	const {isDesktop, isMobile} = useDeviceType();
	const storeLocatorState = useStoreLocatorState();
	const {storeLocator} = storeLocatorState;
	const [physicalStoreName, setPhysicalStoreName] = useState<string>('');
	const productDetails = useProductDetails({ partNumber: id.toString(), physicalStoreName });
	const { product, selection, calculatedPrice, productAttrValues } = productDetails;

	const  productDetailsNs  = useLocalization('productDetail');
	useEffect(() => {
		setPhysicalStoreName(storeLocator.selectedStore?.physicalStoreName ?? '');
	}, [storeLocator.selectedStore]);

	const {releaseDate, webOnly} = useMemo(()=>{
		return{
			releaseDate: productAttrValues.ITNL_ReleaseDate,
			webOnly: productAttrValues.IT_WebDisplayOnly
		}
	},[productAttrValues]);
	
	//Below code is to make Delivery type section floating when scrolled out of view port
	const [isDeliverySectionSticky, setDeliverySectionSticky] = useState(false);
	const STICKY_CLOSED_COOKIE  = 'deliveryStickyClosed';
	
	const handleCloseSticky = () => {
		setCookie(STICKY_CLOSED_COOKIE, "true");
		setDeliverySectionSticky(false);
	};


	useEffect(() => {

		const emptySpace = document.querySelector<HTMLElement>('#empty_space_place_holder');

		// If user already dismissed sticky in this session, never enable it again
		if (getCookie(STICKY_CLOSED_COOKIE) === 'true') {
			setDeliverySectionSticky(false);
			if (isMobile && emptySpace?.style?.height) {
				emptySpace.style.height = '0px';
			}
			return;
		}

		const ccSection = document.querySelector<HTMLElement>('#ccSectionObsRef');
		const footer = document.querySelector<HTMLElement>('footer');
		const addToCart = document.querySelector<HTMLElement>('#add_product_to_cart_section');

		if (!ccSection || !footer) return;

		const handleScroll = () => {

			if (getCookie(STICKY_CLOSED_COOKIE) === 'true') {
				if (isMobile && emptySpace?.style?.height) {
					emptySpace.style.height = '0px';
				}
				return;
			}

			const secTop = ccSection.getBoundingClientRect().top;
			const footerTop = footer.getBoundingClientRect().top;

			const addToCartHeight = addToCart?.offsetHeight ?? 0;

			let shouldStick = false;

			if (isDesktop) {
				shouldStick = secTop < 0 && footerTop > 200;
			}

			if (isMobile) {
				shouldStick = secTop < 0;
				if (emptySpace) {
					const targetHeight = shouldStick ? `${addToCartHeight}px` : '0px';
					if (emptySpace.style.height !== targetHeight) {
						emptySpace.style.height = targetHeight;
					}
				}
			}

			setDeliverySectionSticky(prev =>
				prev !== shouldStick ? shouldStick : prev
			);
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		handleScroll();

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [isDesktop, isMobile]);


	return selection && product?.partNumber? (
		<ContentProvider value={{...productDetails, ...storeLocatorState, isDeliverySectionSticky}}>
			<Stack sx={PdpProductNameAndDetailsSX}>
				<Stack
					direction={{
						md: 'row',
					}}
					spacing={contentSpacing}
				>
					<Stack spacing={2} sx={{width:'-webkit-fill-available'}}>
						<ProductDetailsDisplay hidePartNumber={true} />
						<PDPBazaarVoiceSummary
								partNumber={product?.partNumber} 
								catentryId={product.id}
							/>
						<ProductDetailsRibbonChips />
						{product.type === 'bundle' ? null : (
							<Stack spacing={2}>
								{releaseDate && <Typography color="#368632">{productDetailsNs.RELEASE_DATE.t()}{ReleaseDateFormat(releaseDate)}</Typography>}
								<ProductDetailsPrice />
								<ProductDetailsPromos /> 
								<ProductDetailsSeller />
								{webOnly && webOnly === 'true' ?
									<>
										<Typography sx={{ fontWeight:700,fontSize:'20px',fontFamily:'chaloops-bold'}}> {productDetailsNs.WebOnlyProductTitle.t()} </Typography>
										<Typography > {productDetailsNs.WebOnlyProductDesc.t()} </Typography>
									</> : 
									<>
										<Box id="ccSectionObsRef" sx={ccSectionObsRefSX({isSticky:isDeliverySectionSticky})}></Box>
										<Stack id="add_product_to_cart_section" sx={PdpAddToCartSectionSX({ isSticky: isDeliverySectionSticky, zIndex })}>
											{isDeliverySectionSticky ? 
												<Button 
													variant='textButton' 
													startIcon={<HighlightOffIcon />} 
													onClick={handleCloseSticky}
													sx={CloseStickA2CartBtnSX}
												></Button>
											: null}
											<PDPClickAndCollect id={id}/>
											<Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={1}>
												<Stack width="85%"><ProductDetailsAddToCart /></Stack>
												{webOnly && webOnly === 'true' ? null :<ProductDetailsAddToWishList />}
											</Stack>
										</Stack>
										{ !releaseDate && 
											<KlarnaPDP calculatedPrice={calculatedPrice} />
										}
									</>
								}								
								<StoreStockRv id={id} />
							</Stack>
						)}
					</Stack>
				</Stack>
			</Stack>
		</ContentProvider>
	) : null;
};
